'use client';

import { createClient } from '@/lib/supabase/client';

export interface Affiliate {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  affiliateCode: string;
  status: 'active' | 'suspended';
  totalEarnings: number;
  totalWithdrawn: number;
  createdAt: string;
}

export interface ReferralPayment {
  id: string;
  affiliateId: string;
  studentId: string;
  studentName: string;
  courseId: string;
  categoryCode: string;
  courseFee: number;
  commissionAmount: number;
  withdrawalType: 'instant' | 'deferred';
  isWithdrawable: boolean;
  createdAt: string;
}

export interface WithdrawalRequest {
  id: string;
  affiliateId: string;
  amount: number;
  mobileMoneyprovider: string;
  mobileMoneyNumber: string;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  adminNote: string;
  requestedAt: string;
  processedAt: string | null;
}

// Short course category code
const SHORT_COURSE_CODE = 'SC';
const SHORT_COURSE_THRESHOLD = 5; // payments before withdrawal unlocks
const COMMISSION_RATE = 0.10; // 10%

function dbToAffiliate(row: any): Affiliate {
  return {
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    phone: row.phone,
    affiliateCode: row.affiliate_code,
    status: row.status,
    totalEarnings: parseFloat(row.total_earnings) || 0,
    totalWithdrawn: parseFloat(row.total_withdrawn) || 0,
    createdAt: row.created_at,
  };
}

function dbToReferralPayment(row: any): ReferralPayment {
  return {
    id: row.id,
    affiliateId: row.affiliate_id,
    studentId: row.student_id,
    studentName: row.student_name,
    courseId: row.course_id,
    categoryCode: row.category_code,
    courseFee: parseFloat(row.course_fee) || 0,
    commissionAmount: parseFloat(row.commission_amount) || 0,
    withdrawalType: row.withdrawal_type,
    isWithdrawable: row.is_withdrawable,
    createdAt: row.created_at,
  };
}

function dbToWithdrawal(row: any): WithdrawalRequest {
  return {
    id: row.id,
    affiliateId: row.affiliate_id,
    amount: parseFloat(row.amount) || 0,
    mobileMoneyprovider: row.mobile_money_provider,
    mobileMoneyNumber: row.mobile_money_number,
    status: row.status,
    adminNote: row.admin_note || '',
    requestedAt: row.requested_at,
    processedAt: row.processed_at || null,
  };
}

/** Generate a unique affiliate code like SKY-AFF-XXXXX */
export function generateAffiliateCode(name: string): string {
  const namePart = name.replace(/\s+/g, '').toUpperCase().slice(0, 3).padEnd(3, 'X');
  const rand = Math.floor(10000 + Math.random() * 90000);
  return `SKY-${namePart}-${rand}`;
}

export const affiliateService = {
  /** Register a new affiliate account */
  async register(data: {
    fullName: string;
    email: string;
    phone: string;
    password: string;
  }): Promise<Affiliate | null> {
    const supabase = createClient();
    const affiliateCode = generateAffiliateCode(data.fullName);
    try {
      const { data: row, error } = await supabase
        .from('affiliates')
        .insert({
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          affiliate_code: affiliateCode,
          // Store password as plain text for simple portal auth (no Supabase auth for affiliates)
          password_hash: data.password,
          status: 'active',
          total_earnings: 0,
          total_withdrawn: 0,
        })
        .select()
        .single();
      if (error) return null;
      return row ? dbToAffiliate(row) : null;
    } catch {
      return null;
    }
  },

  /** Login: look up by email + password */
  async login(email: string, password: string): Promise<Affiliate | null> {
    const supabase = createClient();
    try {
      const { data: row, error } = await supabase
        .from('affiliates')
        .select('*')
        .eq('email', email.toLowerCase())
        .eq('password_hash', password)
        .eq('status', 'active')
        .maybeSingle();
      if (error || !row) return null;
      return dbToAffiliate(row);
    } catch {
      return null;
    }
  },

  /** Get affiliate by ID */
  async getById(id: string): Promise<Affiliate | null> {
    const supabase = createClient();
    try {
      const { data: row, error } = await supabase
        .from('affiliates')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error || !row) return null;
      return dbToAffiliate(row);
    } catch {
      return null;
    }
  },

  /** Get affiliate by code (for validation during student registration) */
  async getByCode(code: string): Promise<Affiliate | null> {
    const supabase = createClient();
    try {
      const { data: row, error } = await supabase
        .from('affiliates')
        .select('*')
        .eq('affiliate_code', code.toUpperCase())
        .eq('status', 'active')
        .maybeSingle();
      if (error || !row) return null;
      return dbToAffiliate(row);
    } catch {
      return null;
    }
  },

  /** Get all referral payments for an affiliate */
  async getReferralPayments(affiliateId: string): Promise<ReferralPayment[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('referral_payments')
        .select('*')
        .eq('affiliate_id', affiliateId)
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data || []).map(dbToReferralPayment);
    } catch {
      return [];
    }
  },

  /** Record a commission when admin approves a student payment */
  async recordCommission(params: {
    affiliateId: string;
    studentId: string;
    studentName: string;
    courseId: string;
    categoryCode: string;
    courseFee: number;
  }): Promise<boolean> {
    const supabase = createClient();
    const commissionAmount = params.courseFee * COMMISSION_RATE;
    const isShortCourse = params.categoryCode === SHORT_COURSE_CODE;
    const withdrawalType = isShortCourse ? 'deferred' : 'instant';

    try {
      // Count existing short-course payments for this affiliate if SC
      let isWithdrawable = !isShortCourse; // instant for non-SC
      if (isShortCourse) {
        const { count } = await supabase
          .from('referral_payments')
          .select('*', { count: 'exact', head: true })
          .eq('affiliate_id', params.affiliateId)
          .eq('category_code', SHORT_COURSE_CODE);
        // After this insert, count will be (count + 1)
        isWithdrawable = ((count || 0) + 1) >= SHORT_COURSE_THRESHOLD;
      }

      const { error: insertError } = await supabase
        .from('referral_payments')
        .insert({
          affiliate_id: params.affiliateId,
          student_id: params.studentId,
          student_name: params.studentName,
          course_id: params.courseId,
          category_code: params.categoryCode,
          course_fee: params.courseFee,
          commission_amount: commissionAmount,
          withdrawal_type: withdrawalType,
          is_withdrawable: isWithdrawable,
        });
      if (insertError) return false;

      // If SC and threshold just reached, mark all previous SC payments as withdrawable
      if (isShortCourse && isWithdrawable) {
        await supabase
          .from('referral_payments')
          .update({ is_withdrawable: true })
          .eq('affiliate_id', params.affiliateId)
          .eq('category_code', SHORT_COURSE_CODE);
      }

      // Update affiliate total_earnings
      const { data: aff } = await supabase
        .from('affiliates')
        .select('total_earnings')
        .eq('id', params.affiliateId)
        .single();
      const newTotal = (parseFloat(aff?.total_earnings) || 0) + commissionAmount;
      await supabase
        .from('affiliates')
        .update({ total_earnings: newTotal })
        .eq('id', params.affiliateId);

      return true;
    } catch {
      return false;
    }
  },

  /** Get withdrawable balance for an affiliate */
  async getWithdrawableBalance(affiliateId: string): Promise<number> {
    const supabase = createClient();
    try {
      const { data } = await supabase
        .from('referral_payments')
        .select('commission_amount')
        .eq('affiliate_id', affiliateId)
        .eq('is_withdrawable', true);
      const earned = (data || []).reduce((sum: number, r: any) => sum + parseFloat(r.commission_amount), 0);

      const { data: aff } = await supabase
        .from('affiliates')
        .select('total_withdrawn')
        .eq('id', affiliateId)
        .single();
      const withdrawn = parseFloat(aff?.total_withdrawn) || 0;
      return Math.max(0, earned - withdrawn);
    } catch {
      return 0;
    }
  },

  /** Submit a withdrawal request */
  async requestWithdrawal(params: {
    affiliateId: string;
    amount: number;
    provider: string;
    number: string;
  }): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('withdrawal_requests')
        .insert({
          affiliate_id: params.affiliateId,
          amount: params.amount,
          mobile_money_provider: params.provider,
          mobile_money_number: params.number,
          status: 'pending',
        });
      return !error;
    } catch {
      return false;
    }
  },

  /** Get withdrawal requests for an affiliate */
  async getWithdrawalRequests(affiliateId: string): Promise<WithdrawalRequest[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .eq('affiliate_id', affiliateId)
        .order('requested_at', { ascending: false });
      if (error) return [];
      return (data || []).map(dbToWithdrawal);
    } catch {
      return [];
    }
  },

  /** Admin: get all affiliates */
  async getAllAffiliates(): Promise<Affiliate[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('affiliates')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data || []).map(dbToAffiliate);
    } catch {
      return [];
    }
  },

  /** Admin: get all withdrawal requests */
  async getAllWithdrawalRequests(): Promise<WithdrawalRequest[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .order('requested_at', { ascending: false });
      if (error) return [];
      return (data || []).map(dbToWithdrawal);
    } catch {
      return [];
    }
  },

  /** Admin: approve/reject withdrawal */
  async updateWithdrawalStatus(
    requestId: string,
    status: 'approved\' | \'rejected\' | \'paid',
    adminNote?: string
  ): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('withdrawal_requests')
        .update({
          status,
          admin_note: adminNote || '',
          processed_at: new Date().toISOString(),
        })
        .eq('id', requestId);
      if (error) return false;

      // If paid, update affiliate total_withdrawn
      if (status === 'paid') {
        const { data: req } = await supabase
          .from('withdrawal_requests')
          .select('affiliate_id, amount')
          .eq('id', requestId)
          .single();
        if (req) {
          const { data: aff } = await supabase
            .from('affiliates')
            .select('total_withdrawn')
            .eq('id', req.affiliate_id)
            .single();
          const newTotal = (parseFloat(aff?.total_withdrawn) || 0) + parseFloat(req.amount);
          await supabase
            .from('affiliates')
            .update({ total_withdrawn: newTotal })
            .eq('id', req.affiliate_id);
        }
      }
      return true;
    } catch {
      return false;
    }
  },
};
